import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Bug, Lightbulb, MessageSquare, Clock, CheckCircle, 
  AlertTriangle, RefreshCw, ExternalLink, Trash2,
  FileText, Send, User, MessageCircle, ChevronDown, ChevronUp, Palette,
  Sparkles, Wrench, Search, Calendar, ArrowUpDown, Filter, SlidersHorizontal,
  ThumbsUp, ThumbsDown, ClipboardList, TreePine, Building2, Link2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FeedbackHub() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [expandedItem, setExpandedItem] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  
  // Edit dialog state
  const [selectedItem, setSelectedItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [isKnown, setIsKnown] = useState(false);

  // Changelog state
  const [changelogEntries, setChangelogEntries] = useState([]);
  const [changelogLoading, setChangelogLoading] = useState(false);
  const [changelogSearch, setChangelogSearch] = useState('');

  const isAdmin = user?.is_admin || user?.is_sysadmin || user?.is_platform_admin || user?.is_operator_admin;

  useEffect(() => {
    if (user) {
      fetchFeedback();
      fetchStats();
    }
  }, [activeTab, priorityFilter, statusFilter, sortBy, user]);

  // Fetch changelog once on mount for the badge count
  useEffect(() => {
    if (user) fetchChangelog();
  }, [user]);

  const fetchFeedback = async () => {
    if (activeTab === 'changelog') return;
    setLoading(true);
    try {
      let url = `${API}/api/beta-feedback?limit=100&sort_by=${sortBy}`;
      const typeMap = { bugs: 'bug', features: 'feature', suggestions: 'suggestion' };
      if (typeMap[activeTab]) url += `&type=${typeMap[activeTab]}`;
      if (priorityFilter !== 'all') url += `&priority=${priorityFilter}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setFeedback(await res.json());
    } catch (err) {
      console.error('Error fetching feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/beta-feedback/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchChangelog = async () => {
    setChangelogLoading(true);
    try {
      const res = await axios.get(`${API}/api/changelog`);
      setChangelogEntries(res.data);
    } catch (err) {
      console.error('Failed to load changelog:', err);
    } finally {
      setChangelogLoading(false);
    }
  };

  const handleUpdateFeedback = async () => {
    if (!selectedItem) return;
    try {
      const res = await fetch(`${API}/api/beta-feedback/${selectedItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: newStatus || undefined,
          priority: newPriority || undefined,
          is_known_item: isKnown
        })
      });
      if (res.ok) {
        toast.success('Feedback updated');
        setEditDialogOpen(false);
        fetchFeedback();
        fetchStats();
      } else {
        toast.error('Failed to update');
      }
    } catch (err) {
      toast.error('Error updating feedback');
    }
  };

  const handleAddReply = async (feedbackId) => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);
    try {
      const res = await fetch(`${API}/api/beta-feedback/${feedbackId}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message: replyText, is_internal: false })
      });
      if (res.ok) {
        toast.success('Reply added');
        setReplyText('');
        fetchFeedback();
      } else {
        toast.error('Failed to add reply');
      }
    } catch (err) {
      toast.error('Error adding reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this feedback?')) return;
    try {
      const res = await fetch(`${API}/api/beta-feedback/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Feedback deleted');
        fetchFeedback();
        fetchStats();
      }
    } catch (err) {
      toast.error('Error deleting');
    }
  };

  const handleVote = async (feedbackId, direction) => {
    try {
      const res = await fetch(`${API}/api/beta-feedback/${feedbackId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ direction })
      });
      if (res.ok) {
        const data = await res.json();
        setFeedback(prev => prev.map(item => 
          item.id === feedbackId 
            ? { ...item, upvotes: data.upvotes, downvotes: data.downvotes, vote_score: data.vote_score }
            : item
        ));
      }
    } catch (err) {
      toast.error('Error voting');
    }
  };

  const getUserVote = (item) => {
    if (!user || !item.voter_ids) return null;
    const entry = item.voter_ids.find(v => v.startsWith(`${user.id}:`));
    return entry ? entry.split(':')[1] : null;
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setNewStatus(item.status);
    setNewPriority(item.priority);
    setIsKnown(item.is_known_item);
    setEditDialogOpen(true);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug': return <Bug className="h-4 w-4 text-red-500" />;
      case 'feature': return <Lightbulb className="h-4 w-4 text-blue-500" />;
      case 'suggestion': return <MessageSquare className="h-4 w-4 text-green-500" />;
      case 'design': return <Palette className="h-4 w-4 text-purple-500" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = { bug: 'Bug Report', feature: 'Feature Request', suggestion: 'Suggestion', design: 'Design Feedback' };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      open: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-orange-100 text-orange-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      known: 'bg-purple-100 text-purple-800',
      wont_fix: 'bg-gray-100 text-gray-600'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'border-gray-300 text-gray-600',
      medium: 'border-blue-300 text-blue-700',
      high: 'border-orange-300 text-orange-700',
      critical: 'border-red-300 text-red-700'
    };
    return <Badge variant="outline" className={styles[priority] || 'border-gray-300'}>{priority}</Badge>;
  };

  // Changelog helpers
  const filteredChangelog = useMemo(() => {
    if (!changelogSearch.trim()) return changelogEntries;
    const q = changelogSearch.toLowerCase();
    return changelogEntries.filter(c => c.title.toLowerCase().includes(q) || c.type.includes(q));
  }, [changelogSearch, changelogEntries]);

  const groupedChangelog = useMemo(() => {
    const groups = {};
    filteredChangelog.forEach(entry => {
      if (!groups[entry.date]) groups[entry.date] = [];
      groups[entry.date].push(entry);
    });
    return groups;
  }, [filteredChangelog]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getChangelogIcon = (type) => {
    switch (type) {
      case 'feature': return <Sparkles className="w-4 h-4 text-emerald-500" />;
      case 'fix': return <Bug className="w-4 h-4 text-amber-500" />;
      case 'improve': return <Wrench className="w-4 h-4 text-blue-500" />;
      default: return <Sparkles className="w-4 h-4 text-slate-400" />;
    }
  };

  const getChangelogBadge = (type) => {
    const styles = { feature: 'bg-emerald-100 text-emerald-700', fix: 'bg-amber-100 text-amber-700', improve: 'bg-blue-100 text-blue-700' };
    return <Badge className={styles[type] || 'bg-slate-100'}>{type}</Badge>;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Please Log In</h1>
          <p className="text-gray-600 mt-2">Log in to view feedback and updates.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="feedback-hub-title">
              Feedback & Updates
            </h1>
            <p className="text-gray-600 text-sm">Bug reports, feature requests, and platform changelog</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/beta-test-plan">
                <ClipboardList className="h-4 w-4 mr-2" /> QA Test Plan
              </Link>
            </Button>
            <Button variant="outline" onClick={() => { fetchFeedback(); fetchStats(); if (activeTab === 'changelog') fetchChangelog(); }} data-testid="refresh-feedback-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 w-full justify-start bg-white border" data-testid="feedback-tabs">
            <TabsTrigger value="all" data-testid="tab-all">
              All
              {stats && <Badge variant="secondary" className="ml-1.5 text-xs h-5">{stats.total}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="bugs" data-testid="tab-bugs">
              <Bug className="h-3.5 w-3.5 mr-1.5" />
              Bug Reports
              {stats && <Badge variant="secondary" className="ml-1.5 text-xs h-5 bg-red-50 text-red-700">{stats.by_type.bug}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="features" data-testid="tab-features">
              <Lightbulb className="h-3.5 w-3.5 mr-1.5" />
              Feature Requests
              {stats && <Badge variant="secondary" className="ml-1.5 text-xs h-5 bg-blue-50 text-blue-700">{stats.by_type.feature}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="suggestions" data-testid="tab-suggestions">
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
              Suggestions
              {stats && <Badge variant="secondary" className="ml-1.5 text-xs h-5 bg-green-50 text-green-700">{stats.by_type.suggestion}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="changelog" data-testid="tab-changelog">
              <Calendar className="h-3.5 w-3.5 mr-1.5" />
              Changelog
              {changelogEntries.length > 0 && <Badge variant="secondary" className="ml-1.5 text-xs h-5">{changelogEntries.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Filter & Sort Bar (feedback tabs only) */}
          {activeTab !== 'changelog' && (
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white border rounded-lg" data-testid="feedback-filters">
              <div className="flex items-center gap-1.5 text-sm text-gray-500">
                <SlidersHorizontal className="h-4 w-4" />
                <span>Filters:</span>
              </div>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="known">Known Issue</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-1.5 text-sm text-gray-500">
                <ArrowUpDown className="h-4 w-4" />
                <span>Sort:</span>
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="priority">By Priority</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ALL / BUGS / FEATURES / SUGGESTIONS tabs share the same list layout */}
          {['all', 'bugs', 'features', 'suggestions'].map((tabKey) => (
            <TabsContent key={tabKey} value={tabKey} className="mt-0">
              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                </div>
              ) : feedback.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No {tabKey === 'all' ? 'feedback' : tabKey.replace('s', ' reports')} found</p>
                    <p className="text-sm text-gray-400 mt-1">Use the Feedback button to report bugs or suggest features</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {feedback.map((item) => (
                    <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid={`feedback-item-${item.id}`}>
                      <CardContent className="pt-4 pb-3">
                        <div className="flex items-start gap-3">
                          {/* Vote buttons */}
                          <div className="flex flex-col items-center gap-0.5 shrink-0 pt-1" data-testid={`vote-section-${item.id}`}>
                            <button
                              onClick={() => handleVote(item.id, 'up')}
                              className={`p-1 rounded hover:bg-green-50 transition-colors ${getUserVote(item) === 'up' ? 'text-green-600' : 'text-gray-400'}`}
                              data-testid={`vote-up-${item.id}`}
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </button>
                            <span className={`text-sm font-semibold ${(item.vote_score || 0) > 0 ? 'text-green-600' : (item.vote_score || 0) < 0 ? 'text-red-500' : 'text-gray-400'}`}
                              data-testid={`vote-score-${item.id}`}>
                              {item.vote_score || 0}
                            </span>
                            <button
                              onClick={() => handleVote(item.id, 'down')}
                              className={`p-1 rounded hover:bg-red-50 transition-colors ${getUserVote(item) === 'down' ? 'text-red-500' : 'text-gray-400'}`}
                              data-testid={`vote-down-${item.id}`}
                            >
                              <ThumbsDown className="h-4 w-4" />
                            </button>
                          </div>
                          {/* Content */}
                          <div className="flex items-start justify-between flex-1 min-w-0">
                            <div className="flex items-start gap-3 flex-1">
                              {getTypeIcon(item.type)}
                              <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{getTypeLabel(item.type)}</span>
                                {getStatusBadge(item.status)}
                                {getPriorityBadge(item.priority)}
                                {item.is_known_item && (
                                  <Badge variant="outline" className="bg-purple-50 text-purple-700">Known</Badge>
                                )}
                              </div>
                              <h3 className="font-semibold text-gray-900 mt-1">{item.title}</h3>
                              {item.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" /> {item.submitted_by_username}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                                </span>
                                {item.reply_count > 0 && (
                                  <span className="flex items-center gap-1 text-blue-600">
                                    <MessageCircle className="h-3 w-3" /> {item.reply_count} replies
                                  </span>
                                )}
                                {item.url && (
                                  <a href={item.url} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-blue-600 hover:underline">
                                    <ExternalLink className="h-3 w-3" /> Page
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 ml-4 shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                              data-testid={`expand-feedback-${item.id}`}>
                              {expandedItem === item.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                            {isAdmin && (
                              <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}>Edit</Button>
                            )}
                            {(isAdmin || item.submitted_by_id === user?.id) && (
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                          </div>
                        </div>

                        {/* Expanded replies */}
                        {expandedItem === item.id && (
                          <div className="mt-4 pt-4 border-t">
                            {item.replies && item.replies.length > 0 ? (
                              <div className="space-y-3 mb-4">
                                {item.replies.map((reply) => (
                                  <div key={reply.id} className={`p-3 rounded-lg ${
                                    reply.author_id === item.submitted_by_id ? 'bg-blue-50 mr-8' : 'bg-gray-50 ml-8'
                                  }`}>
                                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                                      <User className="h-3 w-3" />
                                      <span className="font-medium">{reply.author_username}</span>
                                      <span>{formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}</span>
                                    </div>
                                    <p className="text-sm text-gray-700">{reply.message}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 mb-4">No replies yet. Be the first to respond!</p>
                            )}
                            <div className="flex gap-2">
                              <Textarea placeholder="Add a reply..." value={replyText}
                                onChange={(e) => setReplyText(e.target.value)} rows={2} className="flex-1" />
                              <Button onClick={() => handleAddReply(item.id)} disabled={submittingReply || !replyText.trim()}>
                                <Send className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}

          {/* CHANGELOG TAB */}
          <TabsContent value="changelog" className="mt-0">
            {/* Changelog search */}
            <Card className="mb-4">
              <CardContent className="pt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search updates..." value={changelogSearch}
                    onChange={(e) => setChangelogSearch(e.target.value)} className="pl-10"
                    data-testid="changelog-search" />
                </div>
                {changelogSearch && (
                  <p className="text-sm text-gray-500 mt-2">
                    Found {filteredChangelog.length} result{filteredChangelog.length !== 1 ? 's' : ''}
                  </p>
                )}
              </CardContent>
            </Card>

            {changelogLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : filteredChangelog.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  {changelogSearch ? 'No updates match your search' : 'No changelog entries yet'}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedChangelog).map(([date, changes]) => (
                  <div key={date}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-gray-200" />
                      <span className="text-sm font-medium text-gray-500 px-2">{formatDate(date)}</span>
                      <div className="h-px flex-1 bg-gray-200" />
                    </div>
                    <div className="space-y-2">
                      {changes.map((change) => (
                        <div key={change.id} className="flex items-center gap-3 py-2 px-3 bg-white rounded-lg border hover:shadow-sm transition-shadow"
                          data-testid={`changelog-entry-${change.id}`}>
                          {getChangelogIcon(change.type)}
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900">{change.title}</span>
                            {change.description && (
                              <p className="text-sm text-gray-500 mt-0.5">{change.description}</p>
                            )}
                          </div>
                          {getChangelogBadge(change.type)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Contribute Section */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              Help Us Grow the Community
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Beyond bug reports and feature requests, you can contribute to our community in other ways:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link 
                to="/submit-park" 
                className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:border-green-400 hover:shadow-sm transition-all"
                data-testid="contribute-park-link"
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TreePine className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-medium text-sm">Suggest a Dog Park</div>
                  <div className="text-xs text-gray-500">Add parks to our directory</div>
                </div>
              </Link>
              
              <Link 
                to="/submit-service" 
                className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:border-blue-400 hover:shadow-sm transition-all"
                data-testid="contribute-service-link"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-sm">Submit a Service</div>
                  <div className="text-xs text-gray-500">Vets, groomers, trainers</div>
                </div>
              </Link>
              
              <Link 
                to="/submissions" 
                className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:border-purple-400 hover:shadow-sm transition-all"
                data-testid="contribute-content-link"
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-sm">Share a Resource</div>
                  <div className="text-xs text-gray-500">Videos, articles, guides</div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Feedback</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="font-medium">{selectedItem.title}</p>
                  <p className="text-sm text-gray-600">{getTypeLabel(selectedItem.type)} - by {selectedItem.submitted_by_username}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="known">Known Issue</SelectItem>
                      <SelectItem value="wont_fix">Won't Fix</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newPriority} onValueChange={setNewPriority}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="isKnown" checked={isKnown}
                    onChange={(e) => setIsKnown(e.target.checked)} className="rounded" />
                  <Label htmlFor="isKnown">Mark as Known Issue</Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateFeedback}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
